import { getPageRange } from '../../../utils/paginationUtils'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

type Props = {
  page: number
  pageSize: number
  totalElements: number
  navAriaLabel: string
  textSizeClass?: string
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
}

const TablePagination = ({
  page,
  pageSize,
  totalElements,
  navAriaLabel,
  textSizeClass = 'text-xl',
  onPageChange,
  onPageSizeChange,
}: Props): JSX.Element | null => {
  if (totalElements <= 0) return null

  const totalPages = Math.ceil(totalElements / pageSize)
  const pageItems = getPageRange(page, totalPages)

  return (
    <nav
      aria-label={navAriaLabel}
      className={`mt-4 flex flex-wrap items-center justify-between gap-3 ${textSizeClass} text-stone-gray`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2">
          Rows per page:{' '}
          <select
            value={pageSize}
            onChange={e => onPageSizeChange(Number(e.target.value))}
            className="rounded border border-soft-gray px-2 py-1 outline-none focus:border-dark-blue"
          >
            {PAGE_SIZE_OPTIONS.map(o => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </label>
        <span aria-live="polite">
          {totalElements} total · page {page + 1} of {Math.max(totalPages, 1)}
        </span>
      </div>
      {totalPages > 1 && (
        <div className="flex flex-wrap items-center gap-1">
          <button
            disabled={page === 0}
            onClick={() => onPageChange(page - 1)}
            aria-label="Previous page"
            className="rounded border border-soft-gray px-3 py-1 hover:bg-just-gray disabled:opacity-40"
          >
            ‹ Prev
          </button>
          {pageItems.map((p, i) =>
            p === 'dots' ? (
              <span
                key={`dots-${pageItems[i - 1] ?? 'start'}-${pageItems[i + 1] ?? 'end'}`}
                className="px-2"
                aria-hidden="true"
              >
                …
              </span>
            ) : (
              <button
                key={p}
                onClick={() => onPageChange(p)}
                aria-current={p === page ? 'page' : undefined}
                aria-label={`Page ${p + 1}`}
                className={
                  p === page
                    ? 'rounded border border-orange bg-orange px-3 py-1 text-white'
                    : 'rounded border border-soft-gray px-3 py-1 hover:bg-just-gray'
                }
              >
                {p + 1}
              </button>
            )
          )}
          <button
            disabled={page >= totalPages - 1}
            onClick={() => onPageChange(page + 1)}
            aria-label="Next page"
            className="rounded border border-soft-gray px-3 py-1 hover:bg-just-gray disabled:opacity-40"
          >
            Next ›
          </button>
        </div>
      )}
    </nav>
  )
}

export default TablePagination
